import {
  DEFAULT_PIPELINE_STAGES,
  PIPELINE_CATEGORY_LABEL,
  PIPELINE_COLORS,
  type Pipeline,
  type PipelineSource,
  type PipelineStageCategory,
} from '@pm/schemas';
import {
  Button,
  Checkbox,
  ErrorState,
  Field,
  ForbiddenState,
  Input,
  LabelCaps,
  Segmented,
  Select,
  Skeleton,
} from '@pm/ui';
import { ArrowDown, ArrowLeft, ArrowUp, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { useAuth, useMe } from '@/lib/auth';
import { useArchivePipeline, usePipeline, useSavePipeline } from '@/lib/pipelines';
import { useUsers, useWorkflowTemplates } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { useUpdateViewPrefs } from '@/lib/view-prefs';

const COLOR_LABEL: Record<(typeof PIPELINE_COLORS)[number], string> = {
  'var(--text-3)': 'Cinza',
  'var(--info)': 'Azul',
  'var(--accent)': 'Destaque',
  'var(--violet)': 'Violeta',
  'var(--warning)': 'Amarelo',
  'var(--danger)': 'Vermelho',
  'var(--success)': 'Verde',
};
const CATEGORIES = Object.keys(PIPELINE_CATEGORY_LABEL) as PipelineStageCategory[];

interface StageDraft {
  key: string;
  id?: string;
  name: string;
  color: string;
  category: PipelineStageCategory;
  taskCount: number;
}

let seq = 0;
const newKey = () => `n${++seq}`;

/** /pipelines/novo e /pipelines/:id/editar (exige pipelines.manage). */
export function PipelineEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const { data, isLoading, isError, error, refetch } = usePipeline(id);

  if (!can('pipelines.manage')) return <ForbiddenState />;
  if (id && isLoading)
    return (
      <div className="flex max-w-[960px] flex-col gap-3">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-control w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  if (id && (isError || !data))
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  return <Editor key={data?.id ?? 'novo'} pipeline={data} />;
}

function Editor({ pipeline }: { pipeline?: Pipeline }) {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const save = useSavePipeline();
  const archive = useArchivePipeline();
  const updatePrefs = useUpdateViewPrefs();
  const editing = Boolean(pipeline);
  const templates = (useWorkflowTemplates().data ?? []).filter((t) => t.published);
  const users = useUsers({ status: 'ACTIVE', per_page: 200 }).data?.data ?? [];

  const [name, setName] = useState(pipeline?.name ?? '');
  const [description, setDescription] = useState(pipeline?.description ?? '');
  const [source, setSource] = useState<PipelineSource>(pipeline?.source ?? 'MANUAL');
  const [workflowId, setWorkflowId] = useState(pipeline?.workflow_template?.id ?? '');
  const [stages, setStages] = useState<StageDraft[]>(() =>
    pipeline
      ? pipeline.stages.map((s) => ({
          key: s.id,
          id: s.id,
          name: s.name,
          color: s.color,
          category: s.category,
          taskCount: s.task_count ?? 0,
        }))
      : DEFAULT_PIPELINE_STAGES.map((s) => ({
          key: newKey(),
          name: s.name,
          color: s.color ?? 'var(--text-3)',
          category: s.category,
          taskCount: 0,
        })),
  );
  const [members, setMembers] = useState<string[]>(
    () => pipeline?.members.map((m) => m.id) ?? [me.user.id],
  );
  const [memberQuery, setMemberQuery] = useState('');
  const [errors, setErrors] = useState<{ name?: string; workflow?: string; stages?: string }>({});
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Colunas removidas com demandas: para onde elas vão (mesma regra do servidor).
  const moves = useMemo(() => {
    if (!pipeline) return [];
    const kept = new Set(stages.map((s) => s.id).filter(Boolean));
    return pipeline.stages
      .filter((s) => !kept.has(s.id) && (s.task_count ?? 0) > 0)
      .map((s) => {
        const target = stages.find((d) => d.category === s.category) ?? stages[0];
        return { from: s.name, count: s.task_count ?? 0, to: target?.name || 'nova coluna' };
      });
  }, [pipeline, stages]);

  const setStage = (key: string, patch: Partial<StageDraft>) =>
    setStages((list) => list.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  const moveStage = (index: number, delta: number) =>
    setStages((list) => {
      const next = [...list];
      const [item] = next.splice(index, 1);
      next.splice(index + delta, 0, item!);
      return next;
    });

  const filteredUsers = users.filter((u) =>
    `${u.name} ${u.email}`.toLowerCase().includes(memberQuery.trim().toLowerCase()),
  );

  const submit = () => {
    const nextErrors: typeof errors = {};
    if (name.trim().length < 2) nextErrors.name = 'Dê um nome ao pipeline.';
    if (!editing && source === 'WORKFLOW' && !workflowId)
      nextErrors.workflow = 'Escolha o workflow.';
    if (source === 'MANUAL' && stages.some((s) => !s.name.trim()))
      nextErrors.stages = 'Toda coluna precisa de nome.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const stageBody =
      source === 'MANUAL'
        ? stages.map((s) => ({
            ...(s.id ? { id: s.id } : {}),
            name: s.name.trim(),
            color: s.color as (typeof PIPELINE_COLORS)[number],
            category: s.category,
          }))
        : undefined;
    const body = editing
      ? {
          name: name.trim(),
          description: description.trim() || null,
          member_ids: members,
          ...(stageBody ? { stages: stageBody } : {}),
        }
      : {
          name: name.trim(),
          description: description.trim() || null,
          source,
          workflow_template_id: source === 'WORKFLOW' ? workflowId : null,
          member_ids: members,
          ...(stageBody ? { stages: stageBody } : {}),
        };
    save.mutate(
      { id: pipeline?.id, body },
      {
        onSuccess: (saved) => {
          toast.success(editing ? 'Pipeline salvo' : 'Pipeline criado', saved.name);
          updatePrefs({ pipelines: { selected: saved.id } });
          void navigate('/pipelines');
        },
        onError: (err) => toast.error(err),
      },
    );
  };

  return (
    <div className="flex max-w-[960px] flex-col gap-gap-lg">
      <div className="flex items-center gap-2 font-mono text-xs text-text-3">
        <Button
          variant="secondary"
          size="sm"
          className="mr-1 font-sans"
          onClick={() => void navigate('/pipelines')}
        >
          <ArrowLeft /> Voltar
        </Button>
        <span>{editing ? 'Configurar pipeline' : 'Novo pipeline'}</span>
      </div>

      <form
        className="flex flex-col gap-gap-lg"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <section className="flex flex-col gap-gap rounded-md border border-border bg-surface-1 p-4">
          <div className="grid gap-gap sm:grid-cols-2">
            <Field label="Nome" htmlFor="pl-name" error={errors.name}>
              <Input
                id="pl-name"
                value={name}
                maxLength={80}
                autoFocus={!editing}
                invalid={Boolean(errors.name)}
                placeholder="Ex.: Conteúdo, Desenvolvimento, Suporte"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field label="Descrição" htmlFor="pl-desc" hint="Aparece embaixo do título do quadro.">
              <Input
                id="pl-desc"
                value={description}
                maxLength={500}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </div>

          {editing ? (
            <p className="text-sm text-text-2">
              Tipo:{' '}
              <span className="text-text-1">
                {source === 'MANUAL'
                  ? 'colunas próprias'
                  : `etapas do workflow ${pipeline?.workflow_template?.name ?? ''}`}
              </span>
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-text-2">Colunas</span>
              <Segmented
                ariaLabel="Tipo de pipeline"
                value={source}
                onChange={setSource}
                options={[
                  { value: 'MANUAL', label: 'Colunas próprias' },
                  { value: 'WORKFLOW', label: 'Etapas de um workflow' },
                ]}
                className="self-start"
              />
              {source === 'WORKFLOW' ? (
                <Field
                  label="Workflow"
                  htmlFor="pl-wf"
                  error={errors.workflow}
                  hint="As colunas são as etapas da versão publicada; o movimento segue as regras do workflow."
                >
                  <Select
                    id="pl-wf"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                  >
                    <option value="">Escolha…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
            </div>
          )}
        </section>

        {source === 'MANUAL' ? (
          <section className="flex flex-col gap-gap rounded-md border border-border bg-surface-1 p-4">
            <div>
              <LabelCaps>Colunas</LabelCaps>
              <p className="mt-1 text-sm text-text-3">
                A categoria define o status da demanda: mover para uma coluna de outra categoria
                inicia, conclui (pedindo aprovação, se exigida) ou pausa a demanda.
              </p>
            </div>
            <ol className="flex flex-col gap-2">
              {stages.map((s, i) => (
                <li key={s.key} className="flex flex-wrap items-center gap-2">
                  <span className="w-5 text-right font-mono text-xs text-text-3">{i + 1}</span>
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ background: s.color }}
                    aria-hidden
                  />
                  <Input
                    aria-label={`Nome da coluna ${i + 1}`}
                    value={s.name}
                    maxLength={60}
                    onChange={(e) => setStage(s.key, { name: e.target.value })}
                    className="min-w-[160px] flex-1"
                  />
                  <Select
                    aria-label={`Categoria da coluna ${i + 1}`}
                    value={s.category}
                    onChange={(e) =>
                      setStage(s.key, { category: e.target.value as PipelineStageCategory })
                    }
                    className="w-[160px]"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {PIPELINE_CATEGORY_LABEL[c]}
                      </option>
                    ))}
                  </Select>
                  <Select
                    aria-label={`Cor da coluna ${i + 1}`}
                    value={s.color}
                    onChange={(e) => setStage(s.key, { color: e.target.value })}
                    className="w-[120px]"
                  >
                    {PIPELINE_COLORS.map((c) => (
                      <option key={c} value={c}>
                        {COLOR_LABEL[c]}
                      </option>
                    ))}
                  </Select>
                  <span className="flex items-center">
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Subir coluna"
                      disabled={i === 0}
                      onClick={() => moveStage(i, -1)}
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Descer coluna"
                      disabled={i === stages.length - 1}
                      onClick={() => moveStage(i, 1)}
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label="Remover coluna"
                      disabled={stages.length === 1}
                      onClick={() => setStages((list) => list.filter((x) => x.key !== s.key))}
                    >
                      <Trash2 />
                    </Button>
                  </span>
                  {s.taskCount > 0 ? (
                    <span className="basis-full pl-7 font-mono text-xs text-text-3">
                      {s.taskCount} {s.taskCount === 1 ? 'demanda' : 'demandas'}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
            {errors.stages ? <p className="text-sm text-danger">{errors.stages}</p> : null}
            {moves.length > 0 ? (
              <p className="rounded border border-[color-mix(in_oklab,var(--warning)_30%,transparent)] bg-warning-soft px-3 py-2 text-sm text-text-1">
                Ao salvar:{' '}
                {moves.map((m) => `${m.count} de "${m.from}" vão para "${m.to}"`).join('; ')}.
              </p>
            ) : null}
            <Button
              size="sm"
              className="self-start"
              disabled={stages.length >= 20}
              onClick={() =>
                setStages((list) => [
                  ...list,
                  {
                    key: newKey(),
                    name: '',
                    color: 'var(--text-3)',
                    category: 'IN_PROGRESS',
                    taskCount: 0,
                  },
                ])
              }
            >
              <Plus /> Adicionar coluna
            </Button>
          </section>
        ) : null}

        <section className="flex flex-col gap-gap rounded-md border border-border bg-surface-1 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1">
              <LabelCaps>Membros · {members.length}</LabelCaps>
              <p className="mt-1 text-sm text-text-3">
                Só membros veem este pipeline (quem gerencia pipelines vê todos). As demandas
                continuam visíveis conforme as permissões de cada pessoa.
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setMembers(users.map((u) => u.id))}>
              Todos
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setMembers([])}>
              Nenhum
            </Button>
          </div>
          <Input
            leading={<Search />}
            placeholder="Buscar pessoa"
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            aria-label="Buscar pessoa"
          />
          <ul className="scroll-subtle grid max-h-72 gap-1 sm:grid-cols-2">
            {filteredUsers.map((u) => (
              <li key={u.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-2">
                  <Checkbox
                    checked={members.includes(u.id)}
                    onCheckedChange={(v) =>
                      setMembers((list) =>
                        v === true ? [...list, u.id] : list.filter((x) => x !== u.id),
                      )
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-sm text-text-1">{u.name}</span>
                  <span className="truncate font-mono text-xs text-text-3">{u.email}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>

        <div className="flex items-center gap-gap">
          {editing ? (
            <Button variant="ghost" onClick={() => setConfirmArchive(true)}>
              <Trash2 /> Arquivar pipeline
            </Button>
          ) : null}
          <Button variant="ghost" className="ml-auto" onClick={() => void navigate('/pipelines')}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {editing ? 'Salvar' : 'Criar pipeline'}
          </Button>
        </div>
      </form>

      {pipeline ? (
        <ConfirmDialog
          open={confirmArchive}
          onOpenChange={setConfirmArchive}
          title="Arquivar pipeline?"
          description={
            <p className="text-sm text-text-2">
              <span className="text-text-1">{pipeline.name}</span> sai da lista de pipelines e as
              demandas dele voltam para o quadro Geral. Nenhuma demanda é excluída.
            </p>
          }
          confirmLabel="Arquivar"
          danger
          loading={archive.isPending}
          onConfirm={() =>
            archive.mutate(pipeline.id, {
              onSuccess: () => {
                toast.success('Pipeline arquivado');
                updatePrefs({ pipelines: { selected: 'general' } });
                void navigate('/pipelines', { replace: true });
              },
              onError: (err) => toast.error(err),
            })
          }
        />
      ) : null}
    </div>
  );
}
